import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserHistory } from "history";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { CompatRouter, Link, NavLink } from "react-router-dom-v5-compat";

import { makeSeneKitProvider } from "@seneca/senekit";

import { queryClient } from "services";
import PushNotificationsProvider from "services/firebase/messaging/PushNotificationsProvider";
import registerClients from "services/registerClients";

import {
  initializeGoogleTagManager,
  pushGlobalValuesToDataLayer,
  pushUserPropertiesToDataLayer
} from "seneca-common/features/analytics/gtm";
import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";

import AppBackground from "features/app-background/AppBackground";
import ResumeSession from "features/app/components/ResumeSession";
import UpdateUserLastVisit from "features/app/components/UpdateUserLastVisit";
import { VersionUpdateHandler } from "features/app/components/VersionUpdateHandler";
import MinVersionParamHandler from "features/app/update/MinVersionParamHandler/MinVersionParamHandler";
import GeneralAppError, {
  SimpleAppError
} from "features/common/components/AppErrors";
import { CroppedImageBackgroundDefs } from "features/common/components/CroppedImageBackground";
import { ErrorBoundary } from "features/common/components/ErrorHandler";
import { PromoContextProvider } from "features/common/components/PromoTemplate/PromoProvider";
import ModalBodyScrollLocker from "features/common/containers/ModalBodyScrollLocker";
import { ModalNodeProvider } from "features/common/modals/components/ModalNodeContext";
import { LastLocationProvider } from "features/common/routing/with-last-location";
import AppThemeProvider from "features/common/themes";
import GlobalCSS from "features/common/themes/GlobalCSS";
import ToastProvider from "features/common/toasts/ToastProvider";
import { TopBarProvider } from "features/common/top-bars";
import {
  getMobileDeviceType,
  isMobile
} from "features/common/utils/functions/mobile";
import DevToolsProvider from "features/dev-tools/Provider";
import { FlagsProvider } from "features/feature-flags";
import { LocalisationProvider } from "features/localisation";
import LocalStorageBlockedProvider from "features/localStorageBlocked";
import NotificationsAnalyticsProvider from "features/notifications/analytics/NotificationsAnalyticsProvider";
import OfflineScreen from "features/offline-screen";
import PWAMobileProvider from "features/pwa/mobile/PWAMobileProvider";

import AppInstanceProvider from "./AppInstanceProvider";
import { useConfig } from "./config";
import populateCypressGlobals from "./cypressGlobals";
import Routes from "./Routes";
import getStore from "./store";

const SeneKitProvider = makeSeneKitProvider({ createPortal, Link, NavLink });

export default function App() {
  return (
    <ErrorBoundary errorBoundaryName="OuterApp" fallback={<SimpleAppError />}>
      <AppProviders>
        <Routes />
      </AppProviders>
    </ErrorBoundary>
  );
}

function AppProviders({ children }: { children: React.ReactElement }) {
  const [history] = useState(() => createBrowserHistory());
  const store = getStore(history);
  const { config, configError } = useConfig();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      registerClients(config, store);
      initializeGoogleTagManager();
      populateCypressGlobals();
      setInitialized(true);
    }
  }, [initialized, store, config]);

  useEffectOnMount(() => {
    pushGlobalValuesToDataLayer({
      appVersion: process.env.REACT_APP_VERSION,
      isMobile: isMobile(),
      mobileDeviceType: getMobileDeviceType()
    });
    pushUserPropertiesToDataLayer({
      appVersion: process.env.REACT_APP_VERSION
    });
  });

  if (configError) return <OfflineScreen />;

  return (
    <Provider store={store}>
      {/* @ts-expect-error  */}
      <Router history={history}>
        <CompatRouter>
          {/* @ts-ignore fix-in-senekit */}
          <SeneKitProvider value={{ createPortal, Link, NavLink }}>
            <QueryClientProvider client={queryClient}>
              <FlagsProvider>
                <ResumeSession initialized={initialized}>
                  <AppThemeProvider>
                    <GlobalCSS />
                    <PushNotificationsProvider>
                      <NotificationsAnalyticsProvider>
                        <DevToolsProvider>
                          <MinVersionParamHandler>
                            <AppInstanceProvider>
                              <UpdateUserLastVisit initialized={initialized}>
                                {/* @ts-ignore */}
                                <ModalBodyScrollLocker>
                                  <ModalNodeProvider>
                                    <LocalStorageBlockedProvider>
                                      <VersionUpdateHandler>
                                        <PWAMobileProvider>
                                          <LocalisationProvider>
                                            <LastLocationProvider>
                                              <TopBarProvider>
                                                <PromoContextProvider>
                                                  <AppBackground>
                                                    <ErrorBoundary
                                                      errorBoundaryName="App"
                                                      fallback={
                                                        <GeneralAppError />
                                                      }
                                                    >
                                                      {children}
                                                    </ErrorBoundary>
                                                  </AppBackground>
                                                </PromoContextProvider>
                                              </TopBarProvider>
                                            </LastLocationProvider>
                                            <ToastProvider />
                                          </LocalisationProvider>
                                        </PWAMobileProvider>
                                      </VersionUpdateHandler>
                                    </LocalStorageBlockedProvider>
                                  </ModalNodeProvider>
                                </ModalBodyScrollLocker>
                              </UpdateUserLastVisit>
                            </AppInstanceProvider>
                          </MinVersionParamHandler>
                          <CroppedImageBackgroundDefs />
                        </DevToolsProvider>
                      </NotificationsAnalyticsProvider>
                    </PushNotificationsProvider>
                  </AppThemeProvider>
                </ResumeSession>
              </FlagsProvider>
            </QueryClientProvider>
          </SeneKitProvider>
        </CompatRouter>
      </Router>
    </Provider>
  );
}
